import { createRouter, createWebHistory, type RouteLocationNormalizedLoaded, type RouteRecordRaw } from 'vue-router'

import { useMenuStore } from '@/stores/menu'
import { getFileName } from '@/utils/file'
import { getAccessToken, HOME_PAGE, LOGIN_PAGE } from '@/utils/platform'
import { deviceInfo } from '@/utils/system'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: () => HOME_PAGE
    }
  ]
})

const isMobile = computed(() => deviceInfo.value.isMobile)
const childrenRoutes: Record<string, RouteRecordRaw> = {}
childrenRoutes['/src/router'] = { children: [] as Array<RouteRecordRaw> } as RouteRecordRaw

const addRoute = (route: RouteRecordRaw, parentRoute?: RouteRecordRaw) => {
  if (route.path.startsWith('/')) {
    // 绝对路径直接挂根节点下
    childrenRoutes['/src/router']?.children?.push(route)
  } else {
    if (parentRoute) {
      if (parentRoute.children) {
        parentRoute.children.push(route)
      } else {
        parentRoute.children = [route]
      }
    }
  }
}

const modules = import.meta.glob(['/src/router/**/*.ts', '!/src/router/index.ts'], { eager: true })
// 收集所有符合条件的路由
const moduleMap: Record<string, RouteRecordRaw[]> = {}
for (const path in modules) {
  const fileName = getFileName(path)
  if (isMobile.value) {
    // 移动端不加载_pc.ts结尾的路由(加载公共路由和_mb.ts路由)
    if (fileName.endsWith('_pc.ts')) {
      continue
    }
  } else {
    // PC端不加载_mb.ts结尾的路由(加载公共路由和_pc.ts路由)
    if (fileName.endsWith('_mb.ts')) {
      continue
    }
  }
  const moduleDefault: RouteRecordRaw[] = (modules[path] as any).default
  // 不带后缀名的路径(即路由的同名目录，用于接收同名目录下的所有路由为子路由)
  const pathWithoutExt = path.substring(0, path.lastIndexOf('.'))
  if (Array.isArray(moduleDefault)) {
    if (moduleDefault.length === 0) {
      continue
    }
    moduleMap[path] = moduleDefault
    childrenRoutes[pathWithoutExt] = moduleDefault[0]!
  } else if (moduleDefault) {
    moduleMap[path] = [moduleDefault]
    childrenRoutes[pathWithoutExt] = moduleDefault
  }
}
// 将路由挂载到对应父路由下
for (const path in moduleMap) {
  const moduleDefault = moduleMap[path]!
  let parentPath = path.substring(0, path.lastIndexOf('/'))
  let parentRoute = childrenRoutes[parentPath]
  if (!parentRoute) {
    // 如果没有父路由，说明同名目录下没有公共路由，则需要根据当前设备类型加载对应后缀的父路由
    parentPath = isMobile.value ? `${parentPath}_mb` : `${parentPath}_pc`
    parentRoute = childrenRoutes[parentPath]
  }
  moduleDefault.forEach((route) => addRoute(route, parentRoute))
}

const routes = childrenRoutes['/src/router']?.children || ([] as RouteRecordRaw[])
routes.forEach((route) => {
  router.addRoute(route)
})

router.beforeEach(async (to) => {
  // login没有设置或设置成true，都表示需要登录才能访问
  if (to.meta.login === undefined || to.meta.login === null || to.meta.login) {
    try {
      await getAccessToken(true, true)
      // 已登录
      window.document.title = to.meta.title
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // 未登录
      if (to.path !== LOGIN_PAGE) {
        // 不是去登录页的话才需要跳转
        let fullPath = to.path
        if (Object.keys(to.query).length) {
          fullPath += `?${Object.keys(to.query)
            .map((key) => `${key}=${to.query[key]}`)
            .join('&')}`
        }
        const redirectUrl = `${LOGIN_PAGE}?redirect=${encodeURIComponent(fullPath)}`
        return redirectUrl
      } else {
        // 本来就是去登录页
        window.document.title = to.meta.title
      }
    }
  } else {
    // 不需要登录就可以继续访问
    window.document.title = to.meta.title
  }
})
router.afterEach((to: RouteLocationNormalizedLoaded) => {
  const menuStore = useMenuStore()
  const { pathItems, menuLeafs } = storeToRefs(menuStore)
  if (menuLeafs.value[to.fullPath]) {
    // 如果路由在菜单中存在，则更新路径导航
    pathItems.value = []
  } else {
    const index = pathItems.value.findIndex((item) => item.path === to.fullPath)
    if (index >= 0) {
      // 如果已经存在该路径，则截断到该路径
      pathItems.value = pathItems.value.slice(0, index)
    }
  }
  pathItems.value.push({
    path: to.fullPath,
    title: to.meta.title as string,
    name: to.name as string,
    component: to.meta.componentName as string
  })
  // cacheComponents.value.push(to.meta.componentName as string)
})

export default router
